<template>
  <span class="cp-help-wrap">
    <b-button
      size="sm"
      variant="outline-secondary"
      title="How to use the Coaching Portal"
      aria-label="Help"
      @click="show_help = true"
    >
      <i class="fa fa-info-circle"></i>
    </b-button>

    <b-modal
      v-model="show_help"
      title="Using the Coaching Portal"
      size="lg"
      ok-only
      scrollable
    >
      <div class="cp-help-body">
        <h4>What this page is</h4>
        <p>
          The Coaching Portal is one queue for driver accountability. When a driver crosses a
          coaching threshold on the Driver Scorecard &mdash; or a driver-tagged event comes in
          from the Event Response Manager &mdash; it becomes a ticket on the <b>Coaching
          Tickets</b> page with an owner and a due date, and you work it to a signed close.
          The <b>Coaching Dashboard</b> measures; the tickets page is where somebody does
          something about it.
        </p>

        <hr/>

        <h4>Working the queue</h4>
        <ul>
          <li>
            The <b>Status</b> filter lands on <b>Queue</b> — the work: everything open or
            scheduled. Each ticket shows the driver, the topic, why it was raised, who owns
            it, and when it's due. Slice the record with the filters: status, type, driver,
            assigned, the raised-date range, or the search box (a bare <b>#12</b> jumps to
            that ticket).
          </li>
          <li>
            A ticket past its due date shows how many days overdue it is. Once it's overdue past
            your fleet's threshold it carries an <b>ESCALATED</b> badge &mdash; those come first.
          </li>
          <li>
            <b>Mark scheduled</b> when the conversation is booked in; it shows as Scheduled
            so the queue stays honest about what still needs a plan.
          </li>
          <li>
            The <b>Assigned</b> filter narrows the list to your own tickets (<b>Mine</b>)
            or any coach's queue — the <b>Assigned to me</b> tile counts your open work,
            and your rows carry a <b>You</b> chip. When someone assigns or reassigns a
            ticket to you, you get an in-app notification that opens it directly.
          </li>
          <li>
            To hand a ticket to another coach, open it and pick the new owner next to
            <b>Assigned to</b> — the handoff is recorded on the ticket's trail.
          </li>
          <li>
            Prefer swim lanes? The <b>Coaching Board</b> shows the same queue as columns —
            drag a card between <b>Open</b> and <b>Scheduled</b> to change its status.
            Dropping a card on <b>Completed</b> opens the sign-off
            form: closing a ticket always requires the written, signed comment. Closed by
            mistake? Drag it back to reopen — that clears the signature so it can be
            worked again, and the reopen goes on the record with your name.
          </li>
          <li>
            A coaching record holds its paperwork. Attach the warning letter, a signed
            acknowledgement, or a retraining certificate (PDF, Word, or a photo) from the
            ticket. Documents can be added after sign-off — the paperwork usually follows
            the conversation — but not removed once the record is signed.
          </li>
        </ul>

        <hr/>

        <h4>Assigning coaching</h4>
        <p>
          The <b>Flagged Drivers</b> page lists everyone currently over a coaching threshold.
          A flag means two things at once: the driver's number is genuinely outside your fleet's
          own target <i>and</i> they rank near the bottom of the active roster on that metric this
          period &mdash; so a flag is a pattern, not one bad clip. Click <b>Assign</b> and the
          driver's actual numbers are attached to the ticket, so the reason it was raised is
          preserved even after the scorecard moves on.
        </p>

        <hr/>

        <h4>Signing off</h4>
        <ul>
          <li>
            Completing a ticket requires a written comment &mdash; at least a sentence on what was
            actually discussed. This is the record that gets read if the decision is ever
            challenged, so write it that way.
          </li>
          <li>
            Your name, role, and the timestamp are captured with the comment, and
            <b>entries can never be edited or deleted</b> once saved. You can always add
            follow-up notes to a closed ticket.
          </li>
          <li>
            Tick <b>&ldquo;The driver acknowledged this in person&rdquo;</b> when that happened
            &mdash; it is recorded on the signature and shown on the ticket, a distinction that
            matters in a dispute.
          </li>
          <li>
            The outcome choices include <b>Dismissed &mdash; not the driver's fault</b>.
            Exonerating a driver is also a record worth keeping; don't leave a bogus ticket open.
          </li>
        </ul>

        <hr/>

        <h4>Recognition</h4>
        <p>
          Coaching that only ever corrects reads as punishment. The <b>Recognition
          Candidates</b> page surfaces the positive mirror of the flags &mdash; Elite-tier drivers and the
          fleet's single best performer on each coached metric. One click adds them to the
          Recognition tab, and you sign it off once it's actually been delivered: in person, a
          huddle shout-out, a reward, or a note. Recognition never counts toward the coaching
          workload numbers.
        </p>

        <hr/>

        <h4>From the Event Response Manager</h4>
        <p>
          When your fleet runs both, driver-tagged events can push tickets straight into this
          queue &mdash; and those tickets link back to the event, so the full story is one click
          away. A repeat incident for a driver who already has an open ticket lands on that
          ticket's trail instead of creating a duplicate.
        </p>

        <hr/>

        <h4>Does coaching actually work?</h4>
        <p>
          The <b>Coaching Dashboard</b> answers with three numbers: <b>coaching
          effectiveness</b> (the share of signed-off tickets where the same driver was <i>not</i>
          re-coached on the same behavior within the window &mdash; the industry baseline is
          about 60%), <b>median time to coach</b> (research says within a week of the event;
          48 hours is the bar to chase), and <b>coverage</b> (the share of raised tickets that
          reached a signed close). Numbers only count once they've had time to mature, so they
          never flatter early.
        </p>

        <hr/>

        <h4>Settings</h4>
        <p>
          Fleet admins can tune the due-date default, the escalation threshold, and the
          repeat window (how long we watch for a coached behavior to come back) under
          <b>Settings</b> on the <b>Coaching Dashboard</b> &mdash; deadlines that decide
          how much work this page asks of you are your fleet's call, not ours.
        </p>
      </div>
    </b-modal>
  </span>
</template>

<script>
// how-to modal for the Coaching Portal, following the actuals_*_help house
// pattern (info-icon button + ok-only scrollable modal). the page used to
// open with a standing description banner; the modal replaces it so the
// guidance is there when wanted and out of the way when not.
export default {
  name: 'coaching_portal_help',
  data() {
    return { show_help: false }
  },
}
</script>

<style scoped>
.cp-help-body h4 { font-size: 1.05rem; margin-top: 4px; }
.cp-help-body p, .cp-help-body li { font-size: 0.95rem; }
.cp-help-body li { margin-bottom: 6px; }
</style>
